import {
    CheckOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { Button, InputNumber, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useFetcher } from 'react-router-dom';
import { SmallSection } from '../../components/design';
import { Currency, FieldWrapper, InputWrapper, SelectInput } from '../../components/inputs';
import { MismatchPopover } from '../../components/data/mismatch-popover.jsx';
import Confirm from '../../components/message/Confirm.jsx';
import { usePurchasebillLineTotals } from '../../hooks/use-purchasebill-line-total.js';
import { GeneralLedgers, VatTypes } from '../../library/constants/dynamic.js';
import { purchaseLedgerEntityIds } from '../../library/constants/general-ledgers.js';
import { getFieldStatus } from '../../library/helpers/forms/index.js';
import { getFieldSource, isNumericMismatch, isTextMismatch } from '../../library/helpers/purchase-bill-mismatch.js';
import { formatPrice } from '../../library/utilities/intl.js';
import Styles from './purchasebills-lines.module.scss';

const mismatchStyle = { backgroundColor: '#ffed09' }

const filterOption = (input, option) => {
    if (!input) return true;
    const label = String(option?.label ?? option?.name ?? '').toLowerCase();
    const value = String(option?.value ?? option?.id ?? '').toLowerCase();
    const query = input.toLowerCase().trim();
    return label.includes(query) || value.includes(query);
};

const getEntityLedgerOptions = (entityId) => {
    // Some entities also draw on another entity's ledgers (UK uses NL's).
    const ledgerEntityIds = purchaseLedgerEntityIds(entityId);
    const forEntity = GeneralLedgers.filter((gl) => ledgerEntityIds.includes(Number(gl.entity_id)) && (gl.category === 2 || gl.category === '2' || gl.category == null));
    const all = forEntity.length > 0 ? forEntity : GeneralLedgers;

    // Priority first, then trade purchase accounts, then everything else.
    const sorted = all.slice().sort((a, b) => {
        if (Boolean(a.priority) !== Boolean(b.priority)) {
            return b.priority ? 1 : -1;
        }
        const isTradeA = a.code && (a.code.startsWith('70') || a.code.startsWith('604') || a.code.startsWith('52') || a.code.startsWith('54') || a.code.startsWith('153'));
        const isTradeB = b.code && (b.code.startsWith('70') || b.code.startsWith('604') || b.code.startsWith('52') || b.code.startsWith('54') || b.code.startsWith('153'));
        if (isTradeA !== isTradeB) {
            return isTradeA ? -1 : 1;
        }
        return (a.code || '').localeCompare(b.code || '', undefined, { numeric: true });
    });

    return sorted.map((gl) => ({
        label: gl.name || gl.ledger_text,
        value: gl.id,
    }));
};

const LineTypes = {
    VENDOR: 1,
    FINISHER: 2,
    TRANSPORT: 3
}

const PurchaseBillLineEdit = ({ line, currency, entity_id, errors = {}, extractedLine, isSystemSourced = false, recomputedVat }) => {
    const { totalPriceExclVat,
        setTotalPriceExclVat,
        visibleVatId,
        setVisibleVatId,
        totalPriceInclVat,
        generalLedgerId } = usePurchasebillLineTotals(line.price_excl_vat, line.vat_type_id, line.general_ledger_id, recomputedVat)
    const [itemText, setItemText] = useState(line.item_text)
    const [quantity, setQuantity] = useState(line.quantity)
    // Manually edited + saved fields always show "Manual" going forward.
    const isFieldManuallyEdited = (fieldName) => !!line.manually_edited_fields?.includes(fieldName)

    const entityLedgerOptions = React.useMemo(() => getEntityLedgerOptions(entity_id), [entity_id]);

    return (
        <div className={Styles.ledgerLine}>
            <div>
                <input type="hidden" name={`[purchaseLine][${line.id}]id`} value={line.id} />
                <MismatchPopover sourceInfo={getFieldSource(itemText, extractedLine?.item_text, isSystemSourced, isTextMismatch, isFieldManuallyEdited('item_text'))}>
                    <InputWrapper
                        name={`[purchaseLine][${line.id}]item_text`}
                        value={itemText}
                        onChange={(e) => setItemText(e.target.value)}
                        errors={errors}
                        errorKey="item_text"
                        style={isTextMismatch(itemText, extractedLine?.item_text) ? mismatchStyle : undefined}
                    />
                </MismatchPopover>
            </div>
            <FieldWrapper name={`[purchaseLine][${line.id}]general_ledger_id`} errors={errors} errorKey="general_ledger_id">
                <SelectInput
                    name={`[purchaseLine][${line.id}]general_ledger_id`}
                    options={entityLedgerOptions}
                    showSearch
                    filterOption={filterOption}
                    popupMatchSelectWidth={false}
                    placeholder="Search General Ledger"
                    defaultValue={generalLedgerId}
                    value={generalLedgerId}
                    status={getFieldStatus('general_ledger_id', errors)}
                />
            </FieldWrapper>
            <FieldWrapper name={`[purchaseLine][${line.id}]quantity`} errors={errors} errorKey="quantity">
                <MismatchPopover sourceInfo={getFieldSource(quantity, extractedLine?.quantity, isSystemSourced, isNumericMismatch, isFieldManuallyEdited('quantity'))}>
                    <InputNumber
                        name={`[purchaseLine][${line.id}]quantity`}
                        value={quantity}
                        onChange={setQuantity}
                        style={{
                            width: '100%',
                            ...(isNumericMismatch(quantity, extractedLine?.quantity) ? mismatchStyle : null),
                        }}
                    />
                </MismatchPopover>
            </FieldWrapper>
            <FieldWrapper name={`[purchaseLine][${line.id}]price_excl_vat`} errors={errors} errorKey="price_excl_vat">
                <MismatchPopover sourceInfo={getFieldSource(totalPriceExclVat, extractedLine?.price_excl_vat, isSystemSourced, isNumericMismatch, isFieldManuallyEdited('price_excl_vat'))} formatValue={(v) => formatPrice(v, currency.code)}>
                    <Currency
                        name={`[purchaseLine][${line.id}]price_excl_vat`}
                        currency={currency.code}
                        value={totalPriceExclVat}
                        onChange={(val) => setTotalPriceExclVat(val)}
                        style={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            ...(isNumericMismatch(totalPriceExclVat, extractedLine?.price_excl_vat) ? mismatchStyle : null),
                        }}
                    />
                </MismatchPopover>
                <input type="hidden" name={`[purchaseLine][${line.id}]price_excl_vat`} value={totalPriceExclVat} />
            </FieldWrapper>
            <FieldWrapper name={`[purchaseLine][${line.id}]vat_type_id`} errors={errors} errorKey="vat_type_id">
                <MismatchPopover sourceInfo={getFieldSource(VatTypes.find(vat => vat.id === visibleVatId)?.percentage, extractedLine?.vat_rate_percent, isSystemSourced, isNumericMismatch, isFieldManuallyEdited('vat_type_id'))} formatValue={(v) => `${v}%`}>
                    <Select
                        options={VatTypes.filter((vat) => Number(vat.entity_id) === Number(entity_id))}
                        fieldNames={{ value: 'id', label: 'description' }}
                        value={visibleVatId}
                        onChange={val => setVisibleVatId(val)}
                        status={getFieldStatus('vat_type_id', errors)}
                        style={{
                            width: '100%',
                            ...(isNumericMismatch(VatTypes.find(vat => vat.id === visibleVatId)?.percentage, extractedLine?.vat_rate_percent) ? mismatchStyle : null),
                        }}
                    />
                </MismatchPopover>
                <input type="hidden" name={`[purchaseLine][${line.id}]vat_type_id`} value={visibleVatId} />
            </FieldWrapper>
            <div>{formatPrice(totalPriceInclVat, currency.code)}</div>
            <div></div>
        </div>
    )
}
PurchaseBillLineEdit.propTypes = {
    line: PropTypes.object.isRequired,
    currency: PropTypes.object.isRequired,
    entity_id: PropTypes.number.isRequired,
    errors: PropTypes.object,
    extractedLine: PropTypes.object,
    isSystemSourced: PropTypes.bool,
    recomputedVat: PropTypes.shape({
        vat_type_id: PropTypes.number,
        general_ledger_id: PropTypes.number,
    }),
}

const PurchaseBillLineView = ({ line, currency, extractedLine, isSystemSourced = false }) => {
    const fetcher = useFetcher()
    const lineTypeText = () => {
        switch (line.type) {
            case LineTypes.FINISHER:
                return <img width="24" src="/icons/box-checked.svg" title="Finisher" />
            case LineTypes.TRANSPORT:
                return <img width="24" src="/icons/truck.svg" title="Transport" />
            case LineTypes.VENDOR:
                return <img width="24" src="/icons/box.svg" title="Vendor" />
            default:
                return <></>
        }
    }
    // Manually edited + saved fields always show "Manual" going forward.
    const isFieldManuallyEdited = (fieldName) => !!line.manually_edited_fields?.includes(fieldName)

    return (
        <div className={`${Styles.ledgerLine} ${Styles.ledgerLineWithIcon}`}>
            <div>{lineTypeText()}</div>
            <div>
                <MismatchPopover sourceInfo={getFieldSource(line.item_text, extractedLine?.item_text, isSystemSourced, isTextMismatch, isFieldManuallyEdited('item_text'))}>
                    {line.item_text}
                </MismatchPopover>
            </div>
            <div>{GeneralLedgers.find(({ id }) => id === line.general_ledger_id)?.name || line.general_ledger_id}</div>
            <div>
                <MismatchPopover sourceInfo={getFieldSource(line.quantity, extractedLine?.quantity, isSystemSourced, isNumericMismatch, isFieldManuallyEdited('quantity'))}>
                    {line.quantity}
                </MismatchPopover>
            </div>
            <div>
                <MismatchPopover sourceInfo={getFieldSource(line.price_excl_vat, extractedLine?.price_excl_vat, isSystemSourced, isNumericMismatch, isFieldManuallyEdited('price_excl_vat'))} formatValue={(v) => formatPrice(v, currency.code)}>
                    {formatPrice(line.price_excl_vat, currency.code)}
                </MismatchPopover>
            </div>
            <div>
                <MismatchPopover sourceInfo={getFieldSource(VatTypes.find(({ id }) => id === line.vat_type_id)?.percentage, extractedLine?.vat_rate_percent, isSystemSourced, isNumericMismatch, isFieldManuallyEdited('vat_type_id'))} formatValue={(v) => `${v}%`}>
                    {VatTypes.find(({ id }) => id === line.vat_type_id)?.description}
                </MismatchPopover>
            </div>
            <div>
                <MismatchPopover sourceInfo={getFieldSource(line.price_incl_vat, extractedLine?.price_incl_vat, isSystemSourced, isNumericMismatch, isFieldManuallyEdited('price_incl_vat'))} formatValue={(v) => formatPrice(v, currency.code)}>
                    {formatPrice(line.price_incl_vat, currency.code)}
                </MismatchPopover>
            </div>
            <div className="actions">

                <Confirm
                    content="Are you sure to delete this line ?"
                    okText="Yes"
                    icon={<DeleteOutlined />}
                    shape='square'
                    type='default'
                    className="ant-btn-actionpanel"
                    onConfirm={() => {
                        fetcher.submit({
                            action: "deleteLine",
                            lineId: line.id
                        }, {
                            method: "POST",
                            encType: "application/json"
                        })
                    }}
                    danger
                    ghost
                />
            </div>
        </div>
    )
}
PurchaseBillLineView.propTypes = {
    line: PropTypes.object.isRequired,
    currency: PropTypes.object.isRequired,
    extractedLine: PropTypes.object,
    isSystemSourced: PropTypes.bool,
}

const PurchaseBillLineAdd = ({ currency, entity_id }) => {
    const { totalPriceExclVat,
        setTotalPriceExclVat,
        visibleVatId,
        setVisibleVatId,
        totalPriceInclVat } = usePurchasebillLineTotals(null, null)

    const fetcher = useFetcher()
    const { errors = null } = fetcher.data || {};
    const [showAddNew, setShowAddNew] = useState(false)

    useEffect(() => {
        console.log(fetcher.data);
        if (fetcher.state === "idle" && fetcher.data === true) {
            //On succcess
            setShowAddNew(false)
            fetcher.data = null; // Reset fetcher data
            setTotalPriceExclVat(null)

        }
    }, [fetcher.state, fetcher.data])

    const isSubmitting = fetcher.state !== "idle";

    const entityLedgerOptions = React.useMemo(() => getEntityLedgerOptions(entity_id), [entity_id]);

    if (!showAddNew) {
        return (
            <div>
                <Button onClick={() => setShowAddNew(true)}>Add new</Button>
            </div>
        )
    }

    return (
        <SmallSection>
            <fetcher.Form
                className={`${Styles.ledgerLine} ${Styles.ledgerLineWithIcon}`}
                method="post"
                onSubmit={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
            >
                <div></div>
                <InputWrapper name="item_text" defaultValue="" errors={errors} />
                <FieldWrapper name="general_ledger_id" errors={errors}>
                    <Select
                        name="general_ledger_id"
                        options={entityLedgerOptions}
                        showSearch
                        filterOption={filterOption}
                        popupMatchSelectWidth={false}
                        placeholder="Search General Ledger"
                        defaultValue={null}
                    />
                </FieldWrapper>
                <FieldWrapper name="quantity" errors={errors}>
                    <InputNumber name="quantity" defaultValue={null} />
                </FieldWrapper>
                <FieldWrapper name="price_excl_vat" errors={errors}>
                    <Currency name="price_excl_vat" currency={currency.code} value={totalPriceExclVat} onChange={val => setTotalPriceExclVat(val)} />
                </FieldWrapper>
                <FieldWrapper name="vat_type_id" errors={errors}>
                    <Select
                        options={VatTypes.filter((vat) => Number(vat.entity_id) === Number(entity_id))}
                        fieldNames={{ value: 'id', label: 'description' }}
                        defaultValue={null}
                        onChange={val => setVisibleVatId(val)}
                        status={getFieldStatus('vat_type_id', errors)}
                    />
                    <input type="hidden" name='vat_type_id' value={visibleVatId} />
                </FieldWrapper>
                <div>{formatPrice(totalPriceInclVat, currency.code)}</div>
                <div className="actions">
                    <Button
                        name='action'
                        value='addLine'
                        icon={<CheckOutlined />}
                        htmlType='submit'
                        type='default'
                        className="ant-btn-actionpanel"
                        shape='square'
                        primary
                        ghost
                        disabled={isSubmitting}
                        loading={isSubmitting}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        htmlType='button'
                        type='default'
                        className="ant-btn-actionpanel"
                        shape='square'
                        danger
                        ghost
                        disabled={isSubmitting}
                        onClick={() => setShowAddNew(false)}
                    />
                </div>

            </fetcher.Form>
        </SmallSection>
    )
}
PurchaseBillLineAdd.propTypes = {
    currency: PropTypes.object.isRequired,
    entity_id: PropTypes.number.isRequired,
}

const PurchaseBillLines = ({ lines, currency, entity_id, edit, errors, extractedLines, vatPreview }) => {
    return (
        <div className={Styles.ledgerLines}>
            <div className={`${Styles.ledgerLineHead}${edit ? '' : ` ${Styles.ledgerLineWithIcon}`}`}>
                {!edit && <div></div>}
                <div>Item</div>
                <div>General Ledger</div>
                <div>Quantity</div>
                <div>Price Excl. VAT</div>
                <div>VAT</div>
                <div>Price Incl. VAT</div>
                <div></div>
            </div>
            {!!lines && lines.map((line, index) => {
                const isSystemSourced = line.item_id != null
                const extractedLine = extractedLines?.[index]
                return (
                    <SmallSection key={line.id} colorLight="#fff2e9" colorDark="#fe7b1c" bodyStyle={{ padding: '0.5em 0' }}>
                        {edit ?
                            <PurchaseBillLineEdit line={line} currency={currency} entity_id={entity_id} errors={errors?.[index]} extractedLine={extractedLine} isSystemSourced={isSystemSourced} recomputedVat={vatPreview?.[line.id]} /> :
                            <PurchaseBillLineView line={line} currency={currency} extractedLine={extractedLine} isSystemSourced={isSystemSourced} />}
                    </SmallSection>
                )
            })}
            {!edit && <PurchaseBillLineAdd line={{}} currency={currency} entity_id={entity_id} />}
        </div>
    )
}

PurchaseBillLines.propTypes = {
    lines: PropTypes.arrayOf(PropTypes.object).isRequired,
    currency: PropTypes.object.isRequired,
    entity_id: PropTypes.number.isRequired,
    edit: PropTypes.bool.isRequired,
    errors: PropTypes.arrayOf(PropTypes.object),
    extractedLines: PropTypes.arrayOf(PropTypes.object),
    vatPreview: PropTypes.object,
}

export { PurchaseBillLines };
